import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Calendar, ChevronLeft, ChevronRight, Clock, MapPin, Users, Video, GraduationCap, Palmtree } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ROLE = {
    main: { label: 'My class', cls: 'bg-[#064E3B] text-white' },
    extra: { label: 'Extra subject', cls: 'bg-[#5B21B6] text-white' },
    substitute: { label: 'Substitute', cls: 'bg-[#B45309] text-white' },
};
const fmt = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IE', { day: 'numeric', month: 'short' });

const Lesson = ({ c }) => {
    const r = ROLE[c.role] || ROLE.main;
    return (
        <div className={`rounded-lg border p-3 space-y-1.5 ${c.holiday ? 'border-dashed border-[#D1D5DB] bg-[#F9FAFB] opacity-70' : 'border-[#E5E7EB] bg-white'}`} data-testid={`timetable-lesson-${c.class_id}`}>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#064E3B] tabular-nums"><Clock className="h-3.5 w-3.5" />{c.start_time}–{c.end_time}</div>
            <p className="font-medium text-[#1F2937] leading-tight">{c.class_name}{c.arabic_name && <span className="text-xs text-[#6B7280] font-normal ml-1" dir="rtl">{c.arabic_name}</span>}</p>
            <p className="text-xs text-[#4B5563]">{c.program_name}{c.branch ? ` · ${c.branch}` : ''}</p>
            <div className="flex flex-wrap items-center gap-1.5">
                <Badge className={`text-[10px] ${r.cls}`} data-testid={`timetable-role-${c.class_id}`}>{r.label}{c.role === 'extra' && c.subjects?.length ? `: ${c.subjects.join(', ')}` : ''}</Badge>
                {c.room && <span className="inline-flex items-center gap-1 text-[10px] text-[#6B7280]"><MapPin className="h-3 w-3" />Room {c.room}</span>}
                <span className="inline-flex items-center gap-1 text-[10px] text-[#6B7280]"><Users className="h-3 w-3" />{c.students}</span>
                {c.zoom_link && <a href={c.zoom_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] text-[#1D4ED8] hover:underline"><Video className="h-3 w-3" />Zoom</a>}
            </div>
            {c.holiday && <p className="text-[10px] text-[#B45309] inline-flex items-center gap-1"><Palmtree className="h-3 w-3" />No class — {c.holiday}</p>}
        </div>
    );
};

const Timetable = () => {
    const [data, setData] = useState(null);
    const [weekStart, setWeekStart] = useState('');
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        setLoading(true);
        axios.get(`${API_URL}/api/attendance/register/timetable`, { params: weekStart ? { week_start: weekStart } : {}, withCredentials: true })
            .then(r => setData(r.data)).catch(() => toast.error('Failed to load timetable')).finally(() => setLoading(false));
    }, [weekStart]);
    const shift = (n) => { const d = new Date(`${data.week_start}T00:00:00`); d.setDate(d.getDate() + n * 7); setWeekStart(d.toISOString().slice(0, 10)); };
    const total = data ? data.days.reduce((n, d) => n + d.classes.filter(c => !c.holiday).length, 0) : 0;
    const days = data ? data.days.filter(d => d.classes.length > 0) : [];
    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="teacher-timetable">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-2"><Calendar className="h-6 w-6 text-[#064E3B]" />My Timetable</h1>
                        <p className="text-[#4B5563]">Your weekly classes — including extra subjects and substitute days</p>
                    </div>
                    {data && <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="timetable-prev"><ChevronLeft className="h-4 w-4" /></Button>
                        <span className="text-sm font-medium tabular-nums min-w-[170px] text-center" data-testid="timetable-range">{fmt(data.week_start)} – {fmt(data.week_end)}</span>
                        <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="timetable-next"><ChevronRight className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setWeekStart('')} data-testid="timetable-today">This week</Button>
                        <Badge variant="outline" data-testid="timetable-count">{total} lesson{total === 1 ? '' : 's'}</Badge>
                    </div>}
                </div>
                {loading || !data ? <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]" /></div> : days.length === 0 ? (
                    <Card className="card-surface"><CardContent className="flex flex-col items-center justify-center py-12" data-testid="timetable-empty"><GraduationCap className="h-12 w-12 text-[#4B5563]/30 mb-4" /><p className="text-[#4B5563]">No classes this week</p><Link to="/teacher/students" className="text-sm text-[#064E3B] underline mt-2">See my students</Link></CardContent></Card>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {days.map(d => (
                            <Card key={d.date} className={`card-surface ${d.is_today ? 'ring-2 ring-[#064E3B]' : ''}`} data-testid={`timetable-day-${d.weekday}`}>
                                <CardContent className="p-4 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <p className="font-semibold text-[#1F2937] font-['Outfit']">{d.weekday} <span className="text-sm text-[#6B7280] font-normal">{fmt(d.date)}</span></p>
                                        {d.is_today && <Badge className="bg-[#064E3B]">Today</Badge>}
                                    </div>
                                    {d.classes.map(c => <Lesson key={`${c.class_id}-${d.date}`} c={c} />)}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default Timetable;
